import { Component, createRef } from 'react';
import styles from './code-block.module.scss';

export interface CodeBlockProps {
    code: string;
    // Prism language id (e.g. 'css', 'json', 'yaml', 'xml', 'javascript').
    // Pass null/undefined to skip highlighting and render plain text.
    language?: string | null;
    showLineNumbers?: boolean;
    wrapLines?: boolean;
    maxHeight?: number | string;
    minHeight?: number | string;
    // Render a built-in Copy button in the top-right corner of the block.
    copyable?: boolean;
    // Optional aria-label for the block.
    ariaLabel?: string;
}

interface State {
    mounted: boolean;
    mod: any;
    style: any;
    copied: boolean;
}

// Cache the loaded react-syntax-highlighter module + theme + registered
// languages across all CodeBlock instances on the page. The library is
// dynamically imported only on the client so SSR + initial bundle stay thin.
let cachedMod: any = null;
let cachedStyle: any = null;
const registeredLangs = new Set<string>();

// Map our friendly language ids to Prism language ids + the import path of
// their grammar file. Prism's language ids are stable; the import paths
// match the npm package layout.
const LANG_LOADERS: Record<string, () => Promise<any>> = {
    css:        () => import('react-syntax-highlighter/dist/esm/languages/prism/css'),
    scss:       () => import('react-syntax-highlighter/dist/esm/languages/prism/scss'),
    html:       () => import('react-syntax-highlighter/dist/esm/languages/prism/markup'),
    xml:        () => import('react-syntax-highlighter/dist/esm/languages/prism/markup'),
    markup:     () => import('react-syntax-highlighter/dist/esm/languages/prism/markup'),
    javascript: () => import('react-syntax-highlighter/dist/esm/languages/prism/javascript'),
    js:         () => import('react-syntax-highlighter/dist/esm/languages/prism/javascript'),
    typescript: () => import('react-syntax-highlighter/dist/esm/languages/prism/typescript'),
    ts:         () => import('react-syntax-highlighter/dist/esm/languages/prism/typescript'),
    jsx:        () => import('react-syntax-highlighter/dist/esm/languages/prism/jsx'),
    tsx:        () => import('react-syntax-highlighter/dist/esm/languages/prism/tsx'),
    json:       () => import('react-syntax-highlighter/dist/esm/languages/prism/json'),
    yaml:       () => import('react-syntax-highlighter/dist/esm/languages/prism/yaml'),
    yml:        () => import('react-syntax-highlighter/dist/esm/languages/prism/yaml'),
    toml:       () => import('react-syntax-highlighter/dist/esm/languages/prism/toml'),
    ini:        () => import('react-syntax-highlighter/dist/esm/languages/prism/ini'),
    bash:       () => import('react-syntax-highlighter/dist/esm/languages/prism/bash'),
    shell:      () => import('react-syntax-highlighter/dist/esm/languages/prism/bash'),
    python:     () => import('react-syntax-highlighter/dist/esm/languages/prism/python'),
    java:       () => import('react-syntax-highlighter/dist/esm/languages/prism/java'),
    php:        () => import('react-syntax-highlighter/dist/esm/languages/prism/php'),
    ruby:       () => import('react-syntax-highlighter/dist/esm/languages/prism/ruby'),
    go:         () => import('react-syntax-highlighter/dist/esm/languages/prism/go'),
    rust:       () => import('react-syntax-highlighter/dist/esm/languages/prism/rust'),
    sql:        () => import('react-syntax-highlighter/dist/esm/languages/prism/sql'),
    graphql:    () => import('react-syntax-highlighter/dist/esm/languages/prism/graphql'),
    markdown:   () => import('react-syntax-highlighter/dist/esm/languages/prism/markdown'),
    md:         () => import('react-syntax-highlighter/dist/esm/languages/prism/markdown'),
    properties: () => import('react-syntax-highlighter/dist/esm/languages/prism/properties'),
    nginx:      () => import('react-syntax-highlighter/dist/esm/languages/prism/nginx'),
    apacheconf: () => import('react-syntax-highlighter/dist/esm/languages/prism/apacheconf'),
    hcl:        () => import('react-syntax-highlighter/dist/esm/languages/prism/hcl'),
    csharp:     () => import('react-syntax-highlighter/dist/esm/languages/prism/csharp'),
    kotlin:     () => import('react-syntax-highlighter/dist/esm/languages/prism/kotlin'),
    diff:       () => import('react-syntax-highlighter/dist/esm/languages/prism/diff'),
};

// Normalise some common aliases to canonical Prism ids.
function normaliseLang(lang: string): string {
    const l = lang.toLowerCase();
    if (l === 'env' || l === 'dotenv') return 'bash';                  // approximation
    if (l === 'htaccess' || l === 'apache') return 'apacheconf';
    if (l === 'robots' || l === 'robotstxt' || l === 'robots-txt') return 'bash';
    if (l === 'csv' || l === 'tsv') return ''; // plain text
    if (l === 'text' || l === 'txt' || l === 'plain') return '';
    return l;
}

export class CodeBlock extends Component<CodeBlockProps, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    private mountedFlag = false;
    state: State = { mounted: false, mod: cachedMod, style: cachedStyle, copied: false };

    async componentDidMount() {
        this.mountedFlag = true;
        // Defer to next tick so the SSR pre renders first and there's no
        // hydration mismatch.
        await this.loadModule();
        await this.ensureLanguage(this.props.language);
        if (this.mountedFlag) this.setState({ mounted: true });
    }

    async componentDidUpdate(prev: CodeBlockProps) {
        if (prev.language !== this.props.language) {
            await this.ensureLanguage(this.props.language);
            if (this.mountedFlag) this.forceUpdate();
        }
    }

    componentWillUnmount() {
        this.mountedFlag = false;
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private async loadModule() {
        if (cachedMod && cachedStyle) {
            this.setState({ mod: cachedMod, style: cachedStyle });
            return;
        }
        try {
            const [modImport, themeImport] = await Promise.all([
                import('react-syntax-highlighter/dist/esm/prism-light'),
                // oneDark — clean dark theme that matches our #1e293b base.
                import('react-syntax-highlighter/dist/esm/styles/prism/one-dark'),
            ]);
            cachedMod = (modImport as any).default ?? modImport;
            cachedStyle = (themeImport as any).default ?? themeImport;
            this.setState({ mod: cachedMod, style: cachedStyle });
        } catch (e) {
            // Keep the SSR fallback rendered. No fatal — page still works.
        }
    }

    private async ensureLanguage(lang: string | null | undefined) {
        if (!lang) return;
        const canonical = normaliseLang(lang);
        if (!canonical || registeredLangs.has(canonical)) return;
        const loader = LANG_LOADERS[canonical];
        if (!loader || !cachedMod) return;
        try {
            const grammar = await loader();
            cachedMod.registerLanguage(canonical, (grammar as any).default ?? grammar);
            registeredLangs.add(canonical);
        } catch (e) {
            // ignore — fall back to plain text
        }
    }

    private onCopy = () => {
        navigator.clipboard.writeText(this.props.code).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => {
                if (this.mountedFlag) this.setState({ copied: false });
            }, 1800);
        });
    };

    render() {
        const { code, language, showLineNumbers, wrapLines, maxHeight, minHeight, copyable, ariaLabel } = this.props;
        const { mounted, mod, style, copied } = this.state;
        const canonical = language ? normaliseLang(language) : '';

        const containerStyle: React.CSSProperties = {
            maxHeight,
            minHeight,
        };

        // Copy button is positioned absolutely inside the wrapper.
        const copyBtn = copyable ? (
            <button
                type="button"
                onClick={this.onCopy}
                className={`${styles.copyBtn} ${copied ? styles.copyBtnSuccess : ''}`}
                aria-label="Copy code"
            >
                {copied ? '✓ Copied' : '📋 Copy'}
            </button>
        ) : null;

        if (!mounted || !mod || !canonical) {
            // SSR or pre-load fallback: plain pre with our theme colors.
            return (
                <div className={styles.wrapper} style={containerStyle}>
                    <pre className={styles.fallback} aria-label={ariaLabel} style={{ minHeight }}>{code}</pre>
                    {copyBtn}
                </div>
            );
        }

        const Highlighter = mod;
        return (
            <div className={styles.wrapper} style={containerStyle}>
                <Highlighter
                    language={canonical}
                    style={style}
                    showLineNumbers={showLineNumbers}
                    wrapLongLines={wrapLines}
                    customStyle={{
                        margin: 0,
                        padding: '0.85rem 1rem',
                        background: '#1e293b',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        fontSize: '0.85rem',
                        lineHeight: 1.6,
                        maxHeight,
                        minHeight,
                        overflow: 'auto',
                    }}
                    codeTagProps={{
                        style: {
                            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                            fontSize: '0.85rem',
                        },
                    }}
                    lineNumberStyle={{
                        minWidth: '2.5em',
                        paddingRight: '1em',
                        color: '#64748b',
                        userSelect: 'none',
                    }}
                >
                    {code}
                </Highlighter>
                {copyBtn}
            </div>
        );
    }
}

export default CodeBlock;
